import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  CircularProgress,
  Alert,
  Box,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import styles from "./DonorFundraiser.module.css";
import api_endpoints from "../../api";
import DesktopForm from "./DesktopForm";
import MobileForm from "./MobileForm";

const DonorFundraiser = () => {
  const { token } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fundraiserData, setFundraiserData] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [hasAttemptedLoad, setHasAttemptedLoad] = useState(false);

  const fullPath = `${location.pathname}${location.search || ""}${location.hash || ""}`;
  const uuidMatch = fullPath.match(/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/);
  const extractedToken = uuidMatch ? uuidMatch[0] : "";
  const effectiveToken = token || extractedToken;

  useEffect(() => {
    if (!extractedToken) return;

    const canonicalPath = `/donor-fundraiser/${extractedToken}`;
    if (location.pathname !== canonicalPath) {
      navigate(canonicalPath, { replace: true });
    }
  }, [extractedToken, location.pathname, navigate]);

  useEffect(() => {
    // Only proceed if we have a token
    if (!effectiveToken) {
      return;
    }

    setHasAttemptedLoad(true);
    setLoading(true);
    setError(null);

    const fetchFundraiserData = async () => {
      try {
        const { data } = await api_endpoints.getFundraiserLanding(effectiveToken);
        setFundraiserData(data);
      } catch (err) {
        setError(
          err.response?.data?.message ||
            "Failed to load fundraiser information."
        );
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchFundraiserData();
  }, [effectiveToken]);

  // Handle case where token never becomes available
  useEffect(() => {
    if (!hasAttemptedLoad && !effectiveToken) {
      const timer = setTimeout(() => {
        if (!effectiveToken) {
          setError("No fundraiser token provided.");
          setLoading(false);
          setHasAttemptedLoad(true);
        }
      }, 500); // Wait 500ms for token to become available

      return () => clearTimeout(timer);
    }
  }, [effectiveToken, hasAttemptedLoad]);

  return (
    <div
      className={`
        ${styles.main_container}
        flex min-h-screen
      `}
    >
      <div
        className={`
          ${styles.right_panel}
          flex w-full items-center justify-center
        `}
      >
        <div
          className="
            bg-white rounded-3xl shadow-xl

            /* MOBILE: almost full-screen card */
            w-[96vw]
            min-h-[94vh]

            /* TABLET / DESKTOP OVERRIDES */
            sm:w-[92vw] sm:min-h-[90vh]
            md:w-[80vw] md:min-h-[85vh]
            lg:w-[65vw] lg:min-h-[80vh]
            xl:w-[55vw]

            max-w-[900px]


          "
        >

          {/* form*/}
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flex: 1,
              }}
            >
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mt: 4 }}>
              {error}
            </Alert>
          ) : fundraiserData ? (
            isMobile ? (
              <MobileForm fundraiserData={fundraiserData} />
            ) : (
              <DesktopForm fundraiserData={fundraiserData} />
            )
          ) : (
            <Alert severity="warning" sx={{ mt: 4 }}>
              No fundraiser data available.
            </Alert>
          )}
        </div>
      </div>
    </div>
  );
};

export default DonorFundraiser;
